import { ExportGapFilterType } from "@app/exports/enumerations";
import { QESCheckbox, QESRadioButton, QESRadioGroup } from "@app/shared/qes-standards"
import { FC, Fragment, useContext, useMemo, useRef } from "react";
import { countiesFilterRadioOptions } from "./interfaces";
import { VolumeReportsContext } from "./VolumeReportsContext";
import { Roles, UserContext } from "@app/user";
import { toast } from "react-toastify";

export const VolumeReportsCountiesFilters: FC<React.PropsWithChildren<unknown>> = () => {
  const wizardContext = useContext(VolumeReportsContext);
  const userContext = useContext(UserContext);
  const currentServiceArea = useRef("Current Service Area").current;
  const showFillableByMultiple = useMemo(() => {
    return userContext.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
  }, [userContext.roles]);

  const isStateAggregateReport = wizardContext.reportType === "stateaggregate";

  const countiesFilterRadioButtons = useMemo(() => {
    const options = countiesFilterRadioOptions.map((opt) => {
      const key = opt.toLowerCase().replaceAll(" ", "-");
      return (
        <Fragment key={opt}>
          <QESRadioButton
            label={opt}
            testId={`volume-reports__counties-servicing-filters--${key}`}
            onChange={() => {
              wizardContext.setServicingType(opt);
              if (opt === "Opportunities to Expand") {
                toast("\"Append Pattern of Care Providers\" was unchecked. Select Network Providers in the Provider Filter and MA HSD Format to Append Pattern of Care Providers.", {
                  containerId: "z-index-1030",
                  type: "info",
                });
                wizardContext.setAppendPatternOfCareProviders(false);
              }
            }}
            checked={wizardContext.servicingType === opt}
            name="counties-filter"
          />
        </Fragment>
      );
    });
    return options;
  }, [countiesFilterRadioOptions, wizardContext.servicingType]);

  return (
    <section>
      <h2 className="header-m">Counties Filter</h2>
      <p>Please select the counties to include:</p>

      <QESRadioGroup>
        {countiesFilterRadioButtons}
      </QESRadioGroup>

      <p className="mt-4 mb-1">Include</p>
      <QESCheckbox
        label="Active Counties"
        testId="volume-reports__active-counties"
        checked={wizardContext.activeCounties && wizardContext.servicingType === currentServiceArea}
        disabled={wizardContext.servicingType !== currentServiceArea}
        onChange={(e) => { wizardContext.setActiveCounties(e.target.checked) }}
      />
      <QESCheckbox
        label="Pending Counties"
        testId="volume-reports__pending-counties"
        checked={wizardContext.pendingCounties && wizardContext.servicingType === currentServiceArea}
        disabled={wizardContext.servicingType !== currentServiceArea}
        onChange={(e) => { wizardContext.setPendingCounties(e.target.checked) }}
      />
      <QESCheckbox
        label="Approved Counties"
        testId="volume-reports__approved-counties"
        checked={wizardContext.approvedCounties && wizardContext.servicingType === currentServiceArea}
        disabled={wizardContext.servicingType !== currentServiceArea}
        onChange={(e) => { wizardContext.setApprovedCounties(e.target.checked) }}
      />

      {!isStateAggregateReport &&
        <QESRadioGroup
          groupLabel="Include"
          groupContainerClass="mt-4"
        >
          <QESRadioButton
            label="All Counties"
            name="counties-gaps"
            testId="volume-reports__counties-gaps-filters--all"
            value={ExportGapFilterType.All}
            checked={wizardContext.countiesGapsFilter === ExportGapFilterType.All}
            onChange={(e) => wizardContext.setCountiesGapsFilter(e.target.value as ExportGapFilterType)}
          />
          <QESRadioButton
            label="Counties with Gaps"
            name="counties-gaps"
            testId="volume-reports__counties-gaps-filters--gaps"
            value={ExportGapFilterType.Gaps}
            checked={wizardContext.countiesGapsFilter === ExportGapFilterType.Gaps}
            onChange={(e) => wizardContext.setCountiesGapsFilter(e.target.value as ExportGapFilterType)}
          />
          <QESRadioButton
            label="Counties with Gaps Fillable by Single Provider"
            name="counties-gaps"
            testId="volume-reports__counties-gaps-filters--fillable-by-single-provider"
            value={ExportGapFilterType.FillableBySingleProvider}
            checked={wizardContext.countiesGapsFilter === ExportGapFilterType.FillableBySingleProvider}
            onChange={(e) => wizardContext.setCountiesGapsFilter(e.target.value as ExportGapFilterType)}
          />
          <QESRadioButton
            label="Counties with Exceptions"
            name="counties-gaps"
            testId="volume-reports__counties-gaps-filters--exceptions"
            value={ExportGapFilterType.Exceptions}
            checked={wizardContext.countiesGapsFilter === ExportGapFilterType.Exceptions}
            onChange={(e) => wizardContext.setCountiesGapsFilter(e.target.value as ExportGapFilterType)}
          />
          {showFillableByMultiple && (
            <QESRadioButton
              label="Counties with Fillable Gaps"
              name="counties-gaps"
              testId="volume-reports__counties-gaps-filters--fillable-gaps"
              value={ExportGapFilterType.FillableGaps}
              checked={wizardContext.countiesGapsFilter === ExportGapFilterType.FillableGaps}
              onChange={(e) => wizardContext.setCountiesGapsFilter(e.target.value as ExportGapFilterType)}
            />
          )}
        </QESRadioGroup>
      }
    </section >
  );
}